import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import PublicLayout from "@/components/layout/PublicLayout";
import DashboardLayout from "@/components/layout/DashboardLayout";

import Home from "@/pages/marketing/Home";
import HowItWorks from "@/pages/marketing/HowItWorks";
import TrustSafety from "@/pages/marketing/TrustSafety";
import Nonprofits from "@/pages/marketing/Nonprofits";
import About from "@/pages/marketing/About";
import Contact from "@/pages/marketing/Contact";
import FAQ from "@/pages/marketing/FAQ";
import Terms from "@/pages/marketing/Terms";
import Privacy from "@/pages/marketing/Privacy";
import RefundPolicy from "@/pages/marketing/RefundPolicy";
import CookiePolicy from "@/pages/marketing/CookiePolicy";
import AcceptableUse from "@/pages/marketing/AcceptableUse";

import SignIn from "@/pages/auth/SignIn";
import SignUp from "@/pages/auth/SignUp";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";

import Browse from "@/pages/campaign/Browse";
import ProDirectory from "@/pages/campaign/ProDirectory";
import CampaignDetail from "@/pages/campaign/CampaignDetail";
import TeamMemberPage from "@/pages/campaign/TeamMemberPage";
import DonationCheckout from "@/pages/campaign/DonationCheckout";
import DonationConfirmation from "@/pages/campaign/DonationConfirmation";
import ReportCampaign from "@/pages/campaign/ReportCampaign";
import PostDonationAccount from "@/pages/campaign/PostDonationAccount";

import StartFundraiser from "@/pages/organizer/StartFundraiser";
import OrganizerDashboard from "@/pages/organizer/OrganizerDashboard";
import Onboarding from "@/pages/organizer/Onboarding";
import CampaignBuilder from "@/pages/organizer/CampaignBuilder";
import OrganizerCampaigns from "@/pages/organizer/OrganizerCampaigns";
import OrganizerAnalytics from "@/pages/organizer/OrganizerAnalytics";
import PayoutRequest from "@/pages/organizer/PayoutRequest";
import CampaignStatus from "@/pages/organizer/CampaignStatus";

import NonprofitVerify from "@/pages/nonprofit/NonprofitVerify";
import NonprofitDashboard from "@/pages/nonprofit/NonprofitDashboard";

import ReviewQueue from "@/pages/admin/ReviewQueue";
import CampaignReview from "@/pages/admin/CampaignReview";
import PayoutQueue from "@/pages/admin/PayoutQueue";
import Complaints from "@/pages/admin/Complaints";
import Feedback from "@/pages/admin/Feedback";import VideoReview from "@/pages/admin/VideoReview";
import SMSLog from "@/pages/admin/SMSLog";
import AuditLog from "@/pages/admin/AuditLog";
import AdminManagement from "@/pages/admin/AdminManagement";
import PlatformSettings from "@/pages/admin/PlatformSettings";

import BrandGuidelines from "@/pages/brand/BrandGuidelines";
import Embed from "@/pages/campaign/Embed";
import MyDonations from "@/pages/donor/MyDonations";
import PayoutHistory from "@/pages/organizer/PayoutHistory";
import NotFound from "@/pages/NotFound";
import InstallPWA from "@/components/InstallPWA";
import CookieConsent from "@/components/CookieConsent";
import FeedbackWidget from "@/components/FeedbackWidget";

function App() {
  return (
    <div className="App">
      <HelmetProvider>
      <BrowserRouter>
        <AuthProvider>
          <Routes>
            {/* Public + marketing + campaign + auth */}
            <Route element={<PublicLayout />}>
              <Route path="/" element={<Home />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/trust-safety" element={<TrustSafety />} />
              <Route path="/for-nonprofits" element={<Nonprofits />} />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund-policy" element={<RefundPolicy />} />
              <Route path="/cookies" element={<CookiePolicy />} />
              <Route path="/acceptable-use" element={<AcceptableUse />} />

              <Route path="/sign-in" element={<SignIn />} />
              <Route path="/sign-up" element={<SignUp />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />

              <Route path="/browse" element={<Browse />} />
              <Route path="/pro" element={<ProDirectory />} />
              <Route path="/fundraisers/:slug" element={<CampaignDetail />} />
              <Route path="/fundraisers/:slug/team/:memberId" element={<TeamMemberPage />} />
              <Route path="/fundraisers/:slug/donate" element={<DonationCheckout />} />
              <Route path="/donation/confirmation" element={<DonationConfirmation />} />
              <Route path="/donation/create-account" element={<PostDonationAccount />} />
              <Route path="/report-campaign" element={<ReportCampaign />} />
              <Route path="/start-a-fundraiser" element={<StartFundraiser />} />
              <Route path="/account/donations" element={<ProtectedRoute><MyDonations /></ProtectedRoute>} />
            </Route>

            {/* Organizer / nonprofit dashboard (protected) */}
            <Route element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
              <Route path="/organizer/dashboard" element={<OrganizerDashboard />} />
              <Route path="/organizer/analytics" element={<OrganizerAnalytics />} />
              <Route path="/organizer/campaigns" element={<OrganizerCampaigns />} />
              <Route path="/organizer/onboarding" element={<Onboarding />} />
              <Route path="/organizer/campaign-builder" element={<CampaignBuilder />} />
              <Route path="/organizer/campaign-builder/:campaignId" element={<CampaignBuilder />} />
              <Route path="/organizer/campaign-status/:campaignId" element={<CampaignStatus />} />
              <Route path="/organizer/payout-request" element={<PayoutRequest />} />
              <Route path="/organizer/payouts" element={<PayoutHistory />} />
              <Route path="/nonprofit/dashboard" element={<NonprofitDashboard />} />
              <Route path="/nonprofits/verify" element={<NonprofitVerify />} />
            </Route>

            {/* Admin (protected + admin) */}
            <Route element={<ProtectedRoute requireAdmin><DashboardLayout admin /></ProtectedRoute>}>
              <Route path="/admin/reviews" element={<ReviewQueue />} />
              <Route path="/admin/reviews/:campaignId" element={<CampaignReview />} />
              <Route path="/admin/payouts" element={<PayoutQueue />} />
              <Route path="/admin/videos" element={<VideoReview />} />
              <Route path="/admin/complaints" element={<Complaints />} />
              <Route path="/admin/feedback" element={<Feedback />} />
              <Route path="/admin/sms-log" element={<SMSLog />} />
              <Route path="/admin/audit" element={<AuditLog />} />
              <Route path="/admin/admins" element={<ProtectedRoute requireSuperAdmin><AdminManagement /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute requireSuperAdmin><PlatformSettings /></ProtectedRoute>} />
            </Route>

            {/* Standalone embeddable donate widget */}
            <Route path="/embed/:slug" element={<Embed />} />

            {/* Brand guidelines microsite (public, unlinked) */}
            <Route path="/brand-guidelines" element={<BrandGuidelines />} />
            <Route path="/brand-guidelines/:section" element={<BrandGuidelines />} />

            <Route path="*" element={<PublicLayout />}>
              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
          <Toaster position="top-center" richColors />
          <InstallPWA />
          <CookieConsent />
          <FeedbackWidget />
        </AuthProvider>
      </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;
